'use client'

import React, { useState, useCallback } from 'react'
import type { Table as TanStackTable, Column } from '@tanstack/react-table'
import { STYLES_CONFIG, COLUMN_CONFIG } from '../../config'
import type { ColumnDef } from '../../types'

interface Props {
  table: TanStackTable<any>
  columnWidths: Record<string, number>
  originalColumnsDef: ColumnDef[]
  handleMouseDownResize: (e: MouseEvent, colId: string) => void
  onHeaderMouseDown: (evt: MouseEvent, colIndex: number, colId: string) => void
  onHeaderTouchStart: (evt: TouchEvent, colIndex: number, colId: string) => void
  isDarkMode?: boolean
  showColumnFilters?: boolean
}

function ColumnFilter({ column, colDef, isDarkMode }: { column: Column<any, unknown>, colDef?: ColumnDef, isDarkMode: boolean }) {
  const columnType = colDef?.type || 'text'
  const editType = colDef?.editType
  const options = colDef?.options || []

  const [textValue, setTextValue] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '4px 8px',
    fontSize: 12,
    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
    borderRadius: 4,
    backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
    color: isDarkMode ? '#e0e0e0' : '#333',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  }

  const rangeInputStyle: React.CSSProperties = {
    ...inputStyle,
    width: '48%',
    textAlign: 'right',
  }

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='${isDarkMode ? '%23999' : '%23666'}' stroke-width='2'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 6px center',
    paddingRight: 24,
  }

  const handleTextFilter = useCallback((value: string) => {
    setTextValue(value)
    column.setFilterValue(value || undefined)
  }, [column])

  const handleRangeFilter = useCallback((min: string, max: string) => {
    setMinValue(min)
    setMaxValue(max)
    if (!min && !max) {
      column.setFilterValue(undefined)
    } else {
      column.setFilterValue({
        min: min ? parseFloat(min) : undefined,
        max: max ? parseFloat(max) : undefined
      })
    }
  }, [column])

  if (columnType === 'badge' || editType === 'select') {
    return (
      <select
        value={textValue}
        onChange={(e) => handleTextFilter(e.target.value)}
        style={selectStyle}
      >
        <option value="">Todos</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    )
  }

  if (columnType === 'boolean') {
    return (
      <select
        value={textValue}
        onChange={(e) => handleTextFilter(e.target.value)}
        style={selectStyle}
      >
        <option value="">Todos</option>
        <option value="true">Sí</option>
        <option value="false">No</option>
      </select>
    )
  }

  if (columnType === 'numeric' || columnType === 'rating' || columnType === 'progress' || columnType === 'heatmap') {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input
          type="number"
          value={minValue}
          onChange={(e) => handleRangeFilter(e.target.value, maxValue)}
          placeholder="Min"
          style={rangeInputStyle}
        />
        <span style={{ color: isDarkMode ? '#666' : '#999', fontSize: 10 }}>-</span>
        <input
          type="number"
          value={maxValue}
          onChange={(e) => handleRangeFilter(minValue, e.target.value)}
          placeholder="Max"
          style={rangeInputStyle}
        />
      </div>
    )
  }

  if (columnType === 'date' || columnType === 'datetime') {
    return (
      <input
        type="date"
        value={textValue}
        onChange={(e) => handleTextFilter(e.target.value)}
        style={inputStyle}
      />
    )
  }

  if (columnType === 'country') {
    return (
      <input
        type="text"
        value={textValue}
        onChange={(e) => handleTextFilter(e.target.value)}
        placeholder="País..."
        style={inputStyle}
      />
    )
  }

  return (
    <input
      type="text"
      value={textValue}
      onChange={(e) => handleTextFilter(e.target.value)}
      placeholder="Filtrar..."
      style={inputStyle}
    />
  )
}

export default function TableHeader({
  table,
  columnWidths,
  originalColumnsDef,
  handleMouseDownResize,
  onHeaderMouseDown,
  onHeaderTouchStart,
  isDarkMode = false,
  showColumnFilters = true,
}: Props) {
  const headerGroups = table.getHeaderGroups()

  return (
    <thead>
      {headerGroups.map(headerGroup => (
        <React.Fragment key={headerGroup.id}>
          <tr>
            {headerGroup.headers.map((header, colIndex) => {
              const colId = header.column.id
              const isIndexCol = colId === '_selectIndex'
              const colDef = originalColumnsDef.find(c => c.accessorKey === colId)
              const width = columnWidths[colId] ?? colDef?.width ?? COLUMN_CONFIG.DEFAULT_WIDTH

              return (
                <th
                  key={header.id}
                  data-header-index={colIndex}
                  style={{
                    width: isIndexCol ? COLUMN_CONFIG.INDEX_WIDTH : width,
                    minWidth: isIndexCol ? COLUMN_CONFIG.INDEX_WIDTH : COLUMN_CONFIG.MIN_WIDTH,
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    backgroundColor: isIndexCol ? 'var(--color-table-index-header)' : 'var(--color-table-header)',
                    color: 'var(--color-text)',
                    padding: STYLES_CONFIG.header.padding,
                    fontSize: STYLES_CONFIG.header.fontSize,
                    fontWeight: STYLES_CONFIG.header.fontWeight,
                    textAlign: 'left',
                    borderBottom: showColumnFilters ? '1px solid var(--color-divider)' : '2px solid var(--color-divider)',
                    cursor: isIndexCol ? 'default' : 'pointer',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  onClick={() => {
                    if (!isIndexCol && header.column.getCanSort()) {
                      header.column.toggleSorting()
                    }
                  }}
                  onMouseDown={(e) => {
                    const target = e.target as HTMLElement
                    if (!target.classList.contains('resize-handle')) {
                      onHeaderMouseDown(e.nativeEvent, colIndex, colId)
                    }
                  }}
                  onTouchStart={(e) => {
                    if (e.touches.length === 1) {
                      onHeaderTouchStart(e.nativeEvent, colIndex, colId)
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {header.isPlaceholder ? null : header.column.columnDef.header as string}
                    </span>

                    {header.column.getIsSorted() && (
                      <span style={{ fontSize: 10 }}>
                        {header.column.getIsSorted() === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>

                  {!isIndexCol && (
                    <div
                      className="resize-handle"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        cursor: 'col-resize',
                        backgroundColor: 'transparent',
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                        handleMouseDownResize(e.nativeEvent, colId)
                      }}
                    />
                  )}
                </th>
              )
            })}
          </tr>

          {showColumnFilters && (
            <tr>
              {headerGroup.headers.map((header) => {
                const colId = header.column.id
                const isIndexCol = colId === '_selectIndex'
                const colDef = originalColumnsDef.find(c => c.accessorKey === colId)
                const width = columnWidths[colId] ?? colDef?.width ?? COLUMN_CONFIG.DEFAULT_WIDTH
                const canFilter = colDef?.filterable !== false && !isIndexCol

                return (
                  <th
                    key={`filter-${header.id}`}
                    style={{
                      width: isIndexCol ? COLUMN_CONFIG.INDEX_WIDTH : width,
                      minWidth: isIndexCol ? COLUMN_CONFIG.INDEX_WIDTH : COLUMN_CONFIG.MIN_WIDTH,
                      position: 'sticky',
                      top: 32,
                      zIndex: 10,
                      backgroundColor: isIndexCol ? 'var(--color-table-index-header)' : 'var(--color-table-header)',
                      padding: '6px 8px',
                      borderBottom: '2px solid var(--color-divider)',
                    }}
                  >
                    {canFilter && (
                      <ColumnFilter
                        column={header.column}
                        colDef={colDef}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          )}
        </React.Fragment>
      ))}
    </thead>
  )
}
